/**
 * 文件夹 API 服务
 */
import apiClient from './client';
import { keysToCamelCase, keysToSnakeCase } from '@/lib/api-utils';
import type {
  Folder,
  CreateFolderDto,
  UpdateFolderDto,
  FolderCategory,
  FolderDeleteProjectHandling,
  FolderMember,
  FolderMemberListResponse,
  FolderCapabilities,
} from '@/types/folder';
import type { Project } from '@/types/project';

// 后端响应类型（snake_case）
interface FolderListResponse {
  items: Folder[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface FolderTreeNode {
  id: string;
  name: string;
  type?: 'folder' | 'project' | 'subproject'; // ✅ 添加 type 字段
  description?: string;
  color?: string;
  icon?: string;
  coverImageUrl?: string; // ✅ 添加封面图片 URL
  level?: number;
  folderCategory?: number;
  orgId?: string | null;
  orgType?: number | null;
  ownerId?: string;
  createdBy?: string | null;
  memberRole?: Project['memberRole'];
  functionalRoles?: Project['functionalRoles'];
  canCreateScreenplays?: Project['canCreateScreenplays'];
  collabDirection?: Project['collabDirection'];
  creditAllocationPending?: boolean;
  hasConfiguredSubproject?: boolean;
  hasConfiguredMembers?: boolean;
  hasSubprojects?: boolean;
  projectCount?: number;
  subfolderCount?: number;
  createdAt?: string;
  updatedAt?: string;
  capabilities?: FolderCapabilities;
  children?: FolderTreeNode[];
  // ✅ 项目特有字段
  isSubproject?: boolean;
  parentProjectId?: string | null;
  screenplayId?: string | null;
  projects?: Array<{
    id: string;
    name: string;
    contentType: string;
    status: number;
    updatedAt: string;
    createdAt: string;
    orgId?: string | null;
    orgType?: number | null;
    ownerId?: string;
    createdBy?: string | null;
    memberRole?: Project['memberRole'];
    functionalRoles?: Project['functionalRoles'];
    canCreateScreenplays?: Project['canCreateScreenplays'];
    collabDirection?: Project['collabDirection'];
    creditAllocationPending?: boolean;
    parentProjectId?: string | null; // ✅ 添加父项目ID字段
  }>;
}

interface FolderPathItem {
  id: string;
  name: string;
}

export const folderApi = {
  /**
   * 获取文件夹列表
   */
  async getAll(params?: {
    parentId?: string | null;
    folderCategory?: FolderCategory;
    page?: number;
    pageSize?: number;
  }): Promise<FolderListResponse> {
    // 查询参数保持 camelCase，符合 API 规范
    const response = await apiClient.get<FolderListResponse>('/folders', {
      params: params,
    });
    return keysToCamelCase(response);
  },

  /**
   * 获取文件夹树形结构
   */
  async getTree(
    params?: {
      maxDepth?: number;
      includeProjects?: boolean;
      includeSubprojects?: boolean;
      includeFullProjectFields?: boolean;
      scope?: 'mine' | 'accessible';
    },
    request?: { activeOrgId?: string | null }
  ): Promise<{ tree: FolderTreeNode[] }> {
    const activeOrgId = request?.activeOrgId;
    const hasActiveOrgOverride =
      !!request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId');
    const response = await apiClient.get<{
      id: string;
      name: string;
      children?: FolderTreeNode[];
      tree?: FolderTreeNode[];
    }>('/folders/tree', {
      params: params,
      ...(hasActiveOrgOverride
        ? { headers: { 'X-Active-Org-Id': activeOrgId ?? '' } as Record<string, string> }
        : {}),
    });
    const data = keysToCamelCase(response);
    return { tree: data.tree || data.children || [] };
  },

  /**
   * 获取单个文件夹详情
   */
  async getById(
    id: string,
    includeBreadcrumbs = true,
    request?: { activeOrgId?: string | null }
  ): Promise<Folder> {
    const activeOrgId = request?.activeOrgId;
    const hasActiveOrgOverride =
      !!request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId');
    const response = await apiClient.get<Folder>(`/folders/${id}`, {
      params: { includeBreadcrumbs },
      ...(hasActiveOrgOverride
        ? { headers: { 'X-Active-Org-Id': activeOrgId ?? '' } as Record<string, string> }
        : {}),
    });
    return keysToCamelCase(response);
  },

  /**
   * 创建文件夹
   */
  async create(data: CreateFolderDto): Promise<Folder> {
    const requestData = keysToSnakeCase(data);
    console.log('📤 创建文件夹请求数据:', {
      original: data,
      transformed: requestData,
    });
    const response = await apiClient.post<Folder>('/folders', requestData);
    return keysToCamelCase(response);
  },

  /**
   * 更新文件夹
   */
  async update(id: string, data: UpdateFolderDto): Promise<Folder> {
    const requestData = keysToSnakeCase(data);
    const response = await apiClient.put<Folder>(`/folders/${id}`, requestData);
    return keysToCamelCase(response);
  },

  /**
   * 删除文件夹
   */
  async delete(
    id: string,
    cascade = false,
    projectHandling?: FolderDeleteProjectHandling
  ): Promise<void> {
    await apiClient.delete(`/folders/${id}`, {
      params: { cascade, projectHandling },
    });
  },

  /**
   * 移动文件夹
   */
  async move(id: string, targetParentId: string | null): Promise<Folder> {
    const requestData = keysToSnakeCase({ parentFolderId: targetParentId });
    const response = await apiClient.post<Folder>(`/folders/${id}/move`, requestData);
    return keysToCamelCase(response);
  },

  /**
   * 获取文件夹路径（面包屑）
   */
  async getPath(id: string, request?: { activeOrgId?: string | null }): Promise<FolderPathItem[]> {
    const activeOrgId = request?.activeOrgId;
    const hasActiveOrgOverride =
      !!request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId');
    const response = await apiClient.get<{ path: FolderPathItem[] }>(`/folders/${id}/path`, {
      ...(hasActiveOrgOverride
        ? { headers: { 'X-Active-Org-Id': activeOrgId ?? '' } as Record<string, string> }
        : {}),
    });
    const data = keysToCamelCase<{ path: FolderPathItem[] }>(response);
    return data.path;
  },

  /**
   * 克隆文件夹
   */
  async clone(
    id: string,
    options: {
      mode: 'content' | 'recursive';
      targetName?: string;
      targetParentId?: string;
    }
  ): Promise<{
    id: string;
    name: string;
    parentFolderId: string | null;
    clonedFrom: string;
    mode: 'content' | 'recursive';
    itemsCloned: { folders: number; projects: number };
    createdAt: string;
  }> {
    const requestData = keysToSnakeCase(options);
    const response = await apiClient.post<{
      id: string;
      name: string;
      parentFolderId: string | null;
      clonedFrom: string;
      mode: 'content' | 'recursive';
      itemsCloned: { folders: number; projects: number };
      createdAt: string;
    }>(`/folders/${id}/clone`, requestData);
    return keysToCamelCase(response);
  },

  async getMembers(
    id: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<FolderMemberListResponse> {
    const response = await apiClient.get(`/folders/${id}/members`, {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
      },
    });
    return keysToCamelCase<FolderMemberListResponse>(response);
  },

  async addMember(
    id: string,
    data: { userId: string; role: 'owner' | 'editor' | 'viewer' }
  ): Promise<FolderMember> {
    const response = await apiClient.post(`/folders/${id}/members`, data);
    return keysToCamelCase<FolderMember>(response);
  },

  async removeMember(id: string, userId: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/folders/${id}/members/${userId}`);
    return keysToCamelCase<{ message: string }>(response);
  },

  /**
   * 获取文件夹统计信息
   */
  async getStats(
    id: string,
    recursive = false
  ): Promise<{
    folderId: string;
    name: string;
    stats: {
      subfolderCount: number;
      projectCount: number;
      memberCount: number;
      totalSize: number;
      lastActivity?: string;
      resourceCount: Record<string, number>;
    };
    recursive: boolean;
  }> {
    const response = await apiClient.get(`/folders/${id}/stats`, {
      params: { recursive },
    });
    return keysToCamelCase(response);
  },
};
